/**
 * Group Admin API Service
 * API calls for tenant and user management
 */

import api from '@services/api';
import type {
  TenantHierarchy,
  TenantUser,
  CreateTenantPayload,
  UpdateTenantPayload,
  CreateUserPayload,
  UpdateUserPayload,
  TenantsListResponse,
  TenantResponse,
  UsersListResponse,
  UserResponse,
  HierarchyTreeResponse,
  ValidateChildResponse,
  ChatbotShare,
  ShareBotPayload,
  BotSharesListResponse,
  BotShareResponse,
  ShareTargetsResponse,
  ShareableUser,
  ShareableTenant,
  BotAnalytics,
  BotAnalyticsListResponse,
  TenantRenamePreview,
  TenantRenameResult,
} from '../types';

const BASE_URL = '/group-admin';

export const groupAdminService = {
  // ==========================================================================
  // Tenant Operations
  // ==========================================================================

  /**
   * List all tenants the user can manage
   */
  listTenants: async (): Promise<TenantHierarchy[]> => {
    const response = await api.get<TenantsListResponse>(`${BASE_URL}/tenants`);
    return response.data.tenants;
  },

  /**
   * Get tenant details
   */
  getTenant: async (tenantId: string): Promise<TenantHierarchy> => {
    const response = await api.get<TenantResponse>(`${BASE_URL}/tenants/${tenantId}`);
    return response.data.tenant;
  },

  /**
   * Generate a unique tenant code from a name
   */
  generateTenantCode: async (name: string): Promise<string> => {
    const response = await api.post<{ code: string }>(`${BASE_URL}/tenants/generate-code`, { name });
    return response.data.code;
  },

  /**
   * Check if a tenant code is available
   */
  checkTenantCodeAvailable: async (code: string): Promise<boolean> => {
    const response = await api.post<{ code: string; available: boolean }>(`${BASE_URL}/tenants/check-code`, { code });
    return response.data.available;
  },

  /**
   * Create a new child tenant
   */
  createTenant: async (data: CreateTenantPayload): Promise<TenantHierarchy> => {
    const response = await api.post<TenantResponse>(`${BASE_URL}/tenants`, data);
    return response.data.tenant;
  },

  /**
   * Update a tenant
   */
  updateTenant: async (tenantId: string, data: UpdateTenantPayload): Promise<TenantHierarchy> => {
    const response = await api.put<TenantResponse>(`${BASE_URL}/tenants/${tenantId}`, data);
    return response.data.tenant;
  },

  /**
   * What renaming a tenant to `name` would change. Reads only.
   */
  previewTenantRename: async (tenantId: string, name: string): Promise<TenantRenamePreview> => {
    const response = await api.post<TenantRenamePreview>(
      `${BASE_URL}/tenants/${tenantId}/rename-preview`, { name });
    return response.data;
  },

  /**
   * Rename a tenant everywhere. `confirmCurrentName` must be its current name, exactly.
   */
  renameTenant: async (tenantId: string, newName: string, confirmCurrentName: string): Promise<TenantRenameResult> => {
    const response = await api.post<TenantRenameResult>(`${BASE_URL}/tenants/${tenantId}/rename`, {
      new_name: newName,
      confirm_current_name: confirmCurrentName,
    });
    return response.data;
  },

  /**
   * Delete a tenant (soft delete)
   */
  deleteTenant: async (tenantId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/tenants/${tenantId}`);
  },

  // ==========================================================================
  // User Operations
  // ==========================================================================

  /**
   * List users in a tenant
   */
  listUsers: async (tenantId: string): Promise<TenantUser[]> => {
    const response = await api.get<UsersListResponse>(`${BASE_URL}/tenants/${tenantId}/users`);
    return response.data.users;
  },

  /**
   * List all users the current admin can see (own tenant + descendants)
   */
  listAllUsers: async (): Promise<TenantUser[]> => {
    const response = await api.get<UsersListResponse>(`${BASE_URL}/users`);
    return response.data.users;
  },

  /**
   * Create a new user in a tenant
   */
  createUser: async (tenantId: string, data: CreateUserPayload): Promise<TenantUser> => {
    const response = await api.post<UserResponse>(`${BASE_URL}/tenants/${tenantId}/users`, data);
    return response.data.user;
  },

  /**
   * Update a user
   */
  updateUser: async (
    tenantId: string,
    userId: string,
    data: UpdateUserPayload
  ): Promise<TenantUser> => {
    const response = await api.put<UserResponse>(
      `${BASE_URL}/tenants/${tenantId}/users/${userId}`,
      data
    );
    return response.data.user;
  },

  /**
   * Delete a user (hard delete - permanently removes user and all their data)
   */
  deleteUser: async (tenantId: string, userId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/tenants/${tenantId}/users/${userId}`);
  },

  /**
   * Send a password-reset email to a user (1-hour single-use link).
   */
  sendPasswordResetEmail: async (tenantId: string, userId: string): Promise<void> => {
    await api.post(`${BASE_URL}/tenants/${tenantId}/users/${userId}/password-reset-email`);
  },

  /**
   * Directly set a user's password (no email). Kills their existing sessions.
   */
  setUserPassword: async (tenantId: string, userId: string, password: string): Promise<TenantUser> => {
    const response = await api.put<UserResponse>(
      `${BASE_URL}/tenants/${tenantId}/users/${userId}/password`,
      { password }
    );
    return response.data.user;
  },

  /**
   * Invite a user by email: created immediately WITHOUT a password (Pending),
   * receives a 7-day set-password link. Group admins and up only.
   */
  inviteUser: async (
    tenantId: string,
    data: { email: string; role?: string; display_name?: string }
  ): Promise<{ user: TenantUser; email_sent: boolean }> => {
    const response = await api.post<{ user: TenantUser; email_sent: boolean }>(
      `${BASE_URL}/tenants/${tenantId}/users/invite`,
      data
    );
    return response.data;
  },

  /**
   * Re-send the invitation email to a still-pending user (fresh 7-day link).
   */
  resendInvite: async (tenantId: string, userId: string): Promise<void> => {
    await api.post(`${BASE_URL}/tenants/${tenantId}/users/${userId}/resend-invite`);
  },

  // ==========================================================================
  // Hierarchy Operations
  // ==========================================================================

  /**
   * Get the tenant hierarchy tree
   */
  getHierarchyTree: async (rootTenantId?: string): Promise<HierarchyTreeResponse> => {
    const params = rootTenantId ? { root_tenant_id: rootTenantId } : {};
    const response = await api.get<HierarchyTreeResponse>(`${BASE_URL}/hierarchy/tree`, { params });
    return response.data;
  },

  /**
   * Validate if a tenant can have children
   */
  validateCanCreateChild: async (parentTenantId?: string): Promise<ValidateChildResponse> => {
    const params = parentTenantId ? { parent_tenant_id: parentTenantId } : {};
    const response = await api.get<ValidateChildResponse>(`${BASE_URL}/hierarchy/validate-child`, {
      params,
    });
    return response.data;
  },

  // ==========================================================================
  // Bot Sharing Operations
  // ==========================================================================

  /**
   * Share a bot with a user or tenant
   */
  shareBot: async (chatbotId: string, data: ShareBotPayload): Promise<ChatbotShare> => {
    const response = await api.post<BotShareResponse>(`${BASE_URL}/bots/${chatbotId}/share`, data);
    return response.data.share;
  },

  /**
   * List all shares for a bot
   */
  listBotShares: async (chatbotId: string): Promise<ChatbotShare[]> => {
    const response = await api.get<BotSharesListResponse>(`${BASE_URL}/bots/${chatbotId}/shares`);
    return response.data.shares;
  },

  /**
   * Remove a bot share
   */
  removeBotShare: async (chatbotId: string, shareId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/bots/${chatbotId}/shares/${shareId}`);
  },

  /**
   * Get shareable targets (users and tenants the current user can share with)
   */
  getShareTargets: async (): Promise<{ users: ShareableUser[]; tenants: ShareableTenant[] }> => {
    const response = await api.get<ShareTargetsResponse>(`${BASE_URL}/share-targets`);
    return { users: response.data.users, tenants: response.data.tenants };
  },

  /**
   * Get quick share info (own tenant and child tenants for quick share buttons)
   */
  getQuickShareInfo: async (): Promise<QuickShareInfo> => {
    const response = await api.get<QuickShareInfoResponse>(`${BASE_URL}/quick-share-info`);
    return response.data;
  },

  /**
   * Bulk share a bot to multiple tenants
   */
  bulkShareBot: async (chatbotId: string, data: BulkSharePayload): Promise<BulkShareResult> => {
    const response = await api.post<BulkShareResult>(`${BASE_URL}/bots/${chatbotId}/bulk-share`, data);
    return response.data;
  },

  // ==========================================================================
  // Tenant Context Operations
  // ==========================================================================

  getTenantContextPrompt: async (tenantId: string): Promise<{ tenant_id: string; tenant_name: string; context_prompt: string | null; files_context_prompt: string | null }> => {
    const response = await api.get<{ tenant_id: string; tenant_name: string; context_prompt: string | null; files_context_prompt: string | null }>(`${BASE_URL}/tenants/${tenantId}/context-prompt`);
    return response.data;
  },

  updateTenantContextPrompt: async (tenantId: string, contextPrompt: string): Promise<void> => {
    await api.put(`${BASE_URL}/tenants/${tenantId}/context-prompt`, { context_prompt: contextPrompt });
  },

  previewTenantContext: async (tenantId: string): Promise<TenantContextPreviewResponse> => {
    const response = await api.get<TenantContextPreviewResponse>(`${BASE_URL}/tenants/${tenantId}/context-prompt/preview`);
    return response.data;
  },

  listTenantContextFiles: async (tenantId: string): Promise<TenantContextFilesResponse> => {
    const response = await api.get<TenantContextFilesResponse>(`${BASE_URL}/tenants/${tenantId}/context-files`);
    return response.data;
  },

  uploadTenantContextFile: async (tenantId: string, file: File): Promise<{ success: boolean; attachment: TenantContextFile }> => {
    const CHUNK_SIZE = 20 * 1024 * 1024; // 20 MB — safely under Cloud Run's 32 MiB limit

    // Small files: direct upload (unchanged)
    if (file.size <= 30 * 1024 * 1024) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<{ success: boolean; attachment: TenantContextFile }>(
        `${BASE_URL}/tenants/${tenantId}/context-files`,
        formData,
        { timeout: 300_000 },
      );
      return response.data;
    }

    // Large files: chunked upload
    const uploadId = crypto.randomUUID();
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    // Send each chunk
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk, file.name);
      formData.append('upload_id', uploadId);
      formData.append('chunk_index', String(i));
      formData.append('total_chunks', String(totalChunks));

      await api.post(`${BASE_URL}/tenants/${tenantId}/context-files/upload-chunk`, formData, {
        timeout: 120_000,
      });
    }

    // Tell backend to reassemble and process
    const response = await api.post<{ success: boolean; attachment: TenantContextFile }>(
      `${BASE_URL}/tenants/${tenantId}/context-files/complete-chunked`,
      {
        upload_id: uploadId,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        total_chunks: totalChunks,
      },
      { timeout: 300_000 },
    );
    return response.data;
  },

  /** The tab's own door to a file on a tenant's organizational context: it
   *  opens what the tab lists, for whoever may edit that context, and the
   *  Library still answers for the file as before (item 47b). */
  tenantContextFileUrl: (tenantId: string, fileId: string): string =>
    `${BASE_URL}/tenants/${tenantId}/context-files/${fileId}/download`,

  deleteTenantContextFile: async (tenantId: string, attachmentId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/tenants/${tenantId}/context-files/${attachmentId}`);
  },

  updateTenantContextFile: async (tenantId: string, attachmentId: string, data: { include_as_context?: boolean }): Promise<{ success: boolean; attachment: TenantContextFile }> => {
    const response = await api.patch<{ success: boolean; attachment: TenantContextFile }>(
      `${BASE_URL}/tenants/${tenantId}/context-files/${attachmentId}`,
      data,
    );
    return response.data;
  },

  addTenantCloudFiles: async (tenantId: string, provider: string, fileIds: string[]): Promise<{ files: TenantContextFile[]; skipped: { file_id: string; reason: string }[] }> => {
    const response = await api.post(`${BASE_URL}/tenants/${tenantId}/context-files/cloud`, {
      provider,
      file_ids: fileIds,
    });
    return response.data;
  },

  // ==========================================================================
  // Tenant Data Bundles
  // ==========================================================================

  listTenantDataBundles: async (tenantId: string): Promise<TenantDataBundlesResponse> => {
    const response = await api.get<TenantDataBundlesResponse>(`${BASE_URL}/tenants/${tenantId}/data-bundles`);
    return response.data;
  },

  updateTenantDataBundles: async (tenantId: string, bundleIds: string[]): Promise<TenantDataBundlesResponse> => {
    const response = await api.put<TenantDataBundlesResponse>(`${BASE_URL}/tenants/${tenantId}/data-bundles`, { bundle_ids: bundleIds });
    return response.data;
  },

  updateTenantDataBundle: async (tenantId: string, bundleId: string, data: { include_as_context: boolean }): Promise<void> => {
    await api.patch(`${BASE_URL}/tenants/${tenantId}/data-bundles/${bundleId}`, data);
  },

  removeTenantDataBundle: async (tenantId: string, bundleId: string): Promise<void> => {
    await api.delete(`${BASE_URL}/tenants/${tenantId}/data-bundles/${bundleId}`);
  },

  // ==========================================================================
  // Tenant Files Context Prompt
  // ==========================================================================

  updateTenantFilesContextPrompt: async (tenantId: string, prompt: string): Promise<void> => {
    await api.put(`${BASE_URL}/tenants/${tenantId}/files-context-prompt`, { files_context_prompt: prompt });
  },

  regenerateTenantContext: async (tenantId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(`${BASE_URL}/tenants/${tenantId}/regenerate-context`);
    return response.data;
  },

  // ==========================================================================
  // Bot Analytics Operations
  // ==========================================================================

  /**
   * Get bot analytics with role-based visibility
   * - super_admin: sees ALL bots in the system
   * - group_admin/admin: sees only bots they created
   */
  listBotAnalytics: async (): Promise<BotAnalytics[]> => {
    const response = await api.get<BotAnalyticsListResponse>(`${BASE_URL}/bots/analytics`);
    return response.data.bots;
  },

  /**
   * Get daily token usage for a specific bot
   * @param chatbotId - The bot ID to get usage for
   * @param days - Number of days to look back (default 30, max 90)
   */
  getBotDailyUsage: async (chatbotId: string, days: number = 30): Promise<DailyUsageData[]> => {
    const response = await api.get<DailyUsageResponse>(`${BASE_URL}/bots/${chatbotId}/daily-usage`, {
      params: { days }
    });
    return response.data.usage;
  },

  // ==========================================================================
  // Bot Insights Operations
  // ==========================================================================

  /**
   * Get conversation summaries for a bot
   * @param chatbotId - The bot ID to get summaries for
   * @param limit - Maximum number of summaries to return (default 50)
   */
  getBotConversationSummaries: async (chatbotId: string, limit: number = 50): Promise<string[]> => {
    const response = await api.get<{ success: boolean; summaries: string[] }>(
      `${BASE_URL}/bots/${chatbotId}/summaries`,
      { params: { limit } }
    );
    return response.data.summaries;
  },

  /**
   * Ask a question about bot conversations using AI
   * @param chatbotId - The bot ID context
   * @param question - The user's question
   * @param context - The system prompt with summaries context
   * @param messageHistory - Previous messages for chat context
   */
  askBotInsights: async (
    chatbotId: string,
    question: string,
    context: string,
    messageHistory: { role: 'user' | 'assistant'; content: string }[] = []
  ): Promise<string> => {
    const response = await api.post<{ success: boolean; answer: string }>(
      `${BASE_URL}/bots/${chatbotId}/insights`,
      { question, context, message_history: messageHistory }
    );
    return response.data.answer;
  },
};

export interface DailyUsageData {
  date: string;
  tokens: number;
  messages: number;
  conversations: number;
}

interface DailyUsageResponse {
  success: boolean;
  chatbot_id: string;
  days: number;
  usage: DailyUsageData[];
}

// Quick share types
export interface QuickShareTenant {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
}

export interface QuickShareInfo {
  success: boolean;
  role: string;
  own_tenant: QuickShareTenant;
  child_tenants: QuickShareTenant[];
}

interface QuickShareInfoResponse extends QuickShareInfo {}

export interface BulkSharePayload {
  target_tenant_ids: string[];
  permission_level?: 'view' | 'chat' | 'edit';
  can_reshare?: boolean;
}

export interface BulkShareResult {
  success: boolean;
  created: Array<{ tenant_id: string; share_id: string }>;
  failed: Array<{ tenant_id: string; error: string }>;
  skipped: Array<{ tenant_id: string; reason: string }>;
}

// Tenant context preview types
export interface TenantContextPreviewFile {
  file_id: string;
  file_name: string;
  include_as_context: boolean;
  tenant_name: string;
  estimated_tokens: number;
  file_size_bytes: number;
  processing_status: string;
}

export interface TenantContextPreviewResponse {
  tenant_id: string;
  tenant_name: string;
  hierarchical_context: string | null;
  files: TenantContextPreviewFile[];
  files_context_prompt: string | null;
}

// Tenant context file types
export interface TenantContextFile {
  tenant_attachment_id: string;
  tenant_id: string;
  file_id: string;
  attachment_filename: string;
  upload_order: number;
  context_mode: 'direct' | 'rag' | 'pending' | 'error';
  include_as_context: boolean;
  processing_status: string;
  created_at: string | null;
  updated_at: string | null;
  file?: {
    file_name: string;
    file_size_bytes: number;
    file_type: string;
    processing_status: string;
  };
}

export interface TenantContextFilesResponse {
  tenant_id: string;
  tenant_name: string;
  files: TenantContextFile[];
}

// Tenant data bundle types
export interface TenantDataBundle {
  data_bundle_id: string;
  include_as_context: boolean;
  created_at: string | null;
  bundle_name: string;
  bundle_description: string | null;
  bundle_context_prompt: string | null;
  file_count: number;
}

export interface TenantDataBundlesResponse {
  tenant_id: string;
  bundles: TenantDataBundle[];
}

export default groupAdminService;
